// Fixed-point amounts and text parsing.
// Amount = { value, places }: value 15, places 1 means 1.5 — the same representation as
// Swift's (amount, amountDecimalPlaces), so behaviour can be compared 1:1.
import { fold, isDigit, isLetter, normalize } from './text.ts';
import { SEED_UNIT, type CustomUnit, type SeedUnitKey, type UnitRef } from './units.ts';

export type Amount = { value: number; places: number };

export const ONE: Amount = { value: 1, places: 0 };

export function toNumber(a: Amount): number {
  return a.places > 0 ? a.value / 10 ** a.places : a.value;
}

/** Swift fixedPointAmount(Double): round to 3 places, then drop trailing zero places. */
export function fromNumber(n: number): Amount {
  let value = Math.round(n * 1000);
  let places = 3;
  while (places > 0 && value % 10 === 0) {
    value /= 10;
    places -= 1;
  }
  return { value, places };
}

const VULGAR: Record<string, number> = {
  '¼': 0.25, '½': 0.5, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3, '⅛': 0.125, '⅜': 0.375, '⅝': 0.625, '⅞': 0.875,
};

/** One token: "12", "1.5", "3/4", "½", "1½". Mirrors Swift parsedAmountToken (+ vulgar fractions). */
export function parseAmountToken(token: string): Amount | null {
  if ([...token].some(c => c in VULGAR)) {
    let whole = '';
    let frac = 0;
    for (const c of token) {
      if (c in VULGAR) frac += VULGAR[c];
      else if (isDigit(c)) whole += c;
      else return null;
    }
    return fromNumber((whole ? Number(whole) : 0) + frac);
  }
  const slash = token.indexOf('/');
  if (slash >= 0) {
    const num = Number(token.slice(0, slash));
    const den = Number(token.slice(slash + 1));
    if (token.slice(0, slash) === '' || token.slice(slash + 1) === '' || !Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    return fromNumber(num / den);
  }
  const dot = token.indexOf('.');
  const whole = dot >= 0 ? token.slice(0, dot) : token;
  const fraction = dot >= 0 ? token.slice(dot + 1) : '';
  if (!/^\d*$/.test(whole) || !/^\d*$/.test(fraction)) return null;
  const digits = whole + fraction;
  if (digits === '') return null;
  return { value: Number(digits), places: fraction.length };
}

/** Swift IngredientQuantityParser.combinedAmount: "1" + "1/2" → 1.5 */
function combine(whole: Amount, fraction: Amount): Amount {
  const places = Math.max(whole.places, fraction.places);
  return {
    value: whole.value * 10 ** (places - whole.places) + fraction.value * 10 ** (places - fraction.places),
    places,
  };
}

/**
 * Parses what a user types into an amount field: "2", "1.5", "1,5" (German), "1 1/2", "½",
 * "1½". Returns null for empty/invalid input.
 * Δ vs Swift: Swift's popover accepted only "." so "1,5" became 15. Fixed here.
 */
export function parseAmount(text: string): Amount | null {
  const cleaned = text.trim().replace(/(\d),(\d)/g, '$1.$2');
  if (!cleaned) return null;
  const tokens = cleaned.split(/\s+/);
  if (tokens.length === 1) return parseAmountToken(tokens[0]);
  if (tokens.length === 2) {
    const whole = parseAmountToken(tokens[0]);
    const frac = parseAmountToken(tokens[1]);
    if (whole && frac && whole.places === 0 && tokens[1].includes('/')) return combine(whole, frac);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Item search box: "200 g flour 2 whisk" → search text + quantities per phrase.
// Port of Swift ExistingItemSearchScorer.parse.

export type ItemQuantity = { amount: Amount; unit: UnitRef };
export type ParsedItemQuery = {
  searchText: string;
  /** Last quantity typed anywhere (fallback). */
  quantity: ItemQuantity;
  /** Each item phrase with the quantity that was active when it was typed. */
  spans: { text: string; quantity: ItemQuantity }[];
};

function tokenizeQuery(query: string): string[] {
  const raw = query.normalize('NFC').split(/[^\p{L}\p{M}\p{N}/.]+/u).filter(Boolean);
  // Split "200g" → "200","g" at letter/non-letter boundaries (Swift CompactTokenKind).
  return raw.flatMap(token => {
    const parts: string[] = [];
    let current = '';
    let kind: boolean | null = null;
    for (const ch of token) {
      const letter = isLetter(ch);
      if (kind !== null && kind !== letter) {
        parts.push(current);
        current = '';
      }
      current += ch;
      kind = letter;
    }
    if (current) parts.push(current);
    return parts;
  });
}

/** Folded, keeps only alphanumerics plus "/" and "." (Swift normalized(preservesQuantitySeparators:)). */
function normalizeQuantityToken(text: string): string {
  let out = '';
  for (const ch of fold(text)) out += /[\p{L}\p{N}/.½¼¾⅓⅔⅛⅜⅝⅞]/u.test(ch) ? ch : ' ';
  return out.split(' ').filter(Boolean).join(' ');
}

export function parseItemQuery(
  query: string,
  seedUnits: SeedUnitKey[],
  customUnits: CustomUnit[],
): ParsedItemQuery {
  const tokens = tokenizeQuery(query);
  const seedTerms = new Map<string, SeedUnitKey>();
  for (const key of seedUnits) {
    const s = SEED_UNIT[key];
    for (const term of [s.title, s.symbol, ...s.searchTerms]) {
      const n = normalize(term);
      if (n) seedTerms.set(n, key);
    }
  }
  const customTerms = new Map<string, string>();
  for (const c of customUnits) {
    for (const term of [c.title, c.symbol, ...c.searchTerms]) {
      const n = normalize(term);
      if (n) customTerms.set(n, c.id);
    }
  }
  const maxTermTokens = Math.max(1, ...[...seedTerms.keys(), ...customTerms.keys()].map(t => t.split(' ').length));

  const quantity: ItemQuantity = { amount: { ...ONE }, unit: null };
  const active: ItemQuantity = { amount: { ...ONE }, unit: null };
  const spans: ParsedItemQuery['spans'] = [];
  const searchTokens: string[] = [];
  let spanTokens: string[] = [];
  const flush = () => {
    if (spanTokens.length === 0) return;
    spans.push({ text: spanTokens.join(' '), quantity: { amount: { ...active.amount }, unit: active.unit } });
    spanTokens = [];
  };
  const longestMatch = <T,>(terms: Map<string, T>, start: number): { value: T; count: number } | null => {
    const upper = Math.min(maxTermTokens, tokens.length - start);
    for (let count = upper; count >= 1; count--) {
      const hit = terms.get(normalize(tokens.slice(start, start + count).join(' ')));
      if (hit !== undefined) return { value: hit, count };
    }
    return null;
  };

  let i = 0;
  while (i < tokens.length) {
    const amount = parseSearchAmountToken(normalizeQuantityToken(tokens[i]));
    if (amount) {
      flush();
      quantity.amount = amount;
      active.amount = amount;
      i += 1;
      continue;
    }
    const seed = longestMatch(seedTerms, i);
    if (seed) {
      flush();
      quantity.unit = { seed: seed.value };
      active.unit = { seed: seed.value };
      i += seed.count;
      continue;
    }
    const custom = longestMatch(customTerms, i);
    if (custom) {
      flush();
      quantity.unit = { custom: custom.value };
      active.unit = { custom: custom.value };
      i += custom.count;
      continue;
    }
    searchTokens.push(tokens[i]);
    spanTokens.push(tokens[i]);
    i += 1;
  }
  flush();
  return { searchText: searchTokens.join(' '), quantity, spans };
}

/**
 * Search-box amount token. Δ vs Swift: "½"/"1½" parse as 0.5/1.5 (Swift turned any
 * non-ASCII numeral into amount 0).
 */
function parseSearchAmountToken(token: string): Amount | null {
  return parseAmountToken(token);
}

/** The quantity typed next to an item's name (title or synonym), else the fallback quantity. */
export function quantityFor(parsed: ParsedItemQuery, terms: string[]): ItemQuantity {
  for (const span of parsed.spans) {
    const spanText = ` ${normalize(span.text)} `;
    if (terms.some(t => {
      const n = normalize(t);
      return n !== '' && spanText.includes(` ${n} `);
    })) {
      return span.quantity;
    }
  }
  return parsed.quantity;
}
